/**
 * 경로 판정. **React Router 를 넣지 않는다** — 화면이 다섯이고 그중 셋은 서버가 만든
 * URL 로 직접 들어오는 진입점이라 중첩 라우트도 전환 애니메이션도 필요 없다
 * (`docs/09` T7 — "`location.pathname` 스위치로 충분").
 *
 * **순수 함수로 떼는 이유는 테스트다.** 이 패키지는 node 환경에서 테스트한다
 * (jsdom 미도입) — DOM 을 만지는 순간 라우팅 규칙이 검사에서 통째로 빠진다.
 */

import { TV2M_DEEP_LINK_PATH } from '@apm/contracts';

export const PAGES = [
  'watch',
  'channels',
  'login',
  'product',
  'install',
  'done',
  'debug',
  'stb-sim',
] as const;
export type Page = (typeof PAGES)[number];

/**
 * 경로 판정을 가르는 배포 설정.
 *
 * **불리언 둘을 위치 인자로 늘어놓지 않는다.** `resolvePage(path, true, false)` 는 읽는
 * 사람이 무엇이 켜졌는지 알 수 없고, 두 값을 바꿔 넣어도 컴파일이 통과한다.
 */
export interface RouteOptions {
  /** `?debug=1` */
  debug?: boolean;
  /** `VITE_ENABLE_MODE_A_SIM` — STB 모사 화면을 노출하는가 (B11) */
  modeASim?: boolean;
}

/**
 * **`/` 의 뜻이 B7 에서 바뀌었다.** 통합 전에는 리시버의 `/` 가 시청 화면이고 모바일
 * 웹의 `/`(다른 오리진)가 홈이었다. 한 오리진이 된 지금 `/` 는 **시청 화면**이고,
 * 옛 홈의 내용(등록 상태·알림 켜기)은 그 위에 겹치는 설정 시트로 옮겼다 —
 * 없애면 안 된다. Tier 2 가 성립하려면 등록과 구독이 그 전에 끝나 있어야 한다
 * (`docs/07` §4 시나리오 B).
 *
 * Deep Link 경로는 문자열로 다시 적지 않고 `@apm/contracts` 의 `TV2M_DEEP_LINK_PATH` 를
 * 쓴다 — 서버가 그 상수로 링크를 만들고 이 화면이 그것을 받는다 (절대 규칙 10).
 * 어긋나면 증상은 "링크를 눌렀는데 방송이 뜬다" 뿐이라 어느 쪽이 틀렸는지 드러나지 않는다.
 */
const ROUTES: Record<string, Page> = {
  '/': 'watch',
  /**
   * 채널 목록 (v9). **`/` 를 이것으로 바꾸지 않는다** (D8) — 하네스 일곱이 `/?debug=1`
   * 에서 재생을 시작해 `T_freeze` 를 재고, `/` 가 목록이 되면 그 측정이 통째로 사라진다.
   * `/` 는 시청 화면이고 **어느 채널이냐만 달라진다**.
   */
  '/channels': 'channels',
  '/login': 'login',
  [TV2M_DEEP_LINK_PATH]: 'product',
  '/install': 'install',
  '/done': 'done',
  '/debug': 'debug',
  '/sim/stb': 'stb-sim',
};

/**
 * 경로 하나에서 화면 하나를 낸다. 모르는 경로는 시청 화면이다 — 404 화면을 만들지 않는다.
 *
 * ── `/login` 은 경로이면서 경로가 아니다 (B8) ─────────────────
 *
 * 로그인 화면은 대개 **이 표를 거치지 않고** 뜬다. 게이트가 인증 상태만 보고 그리므로,
 * QR 로 온 사람은 주소가 `/install?ref=…` 인 채로 로그인 화면을 본다 — **주소를 바꾸지
 * 않는 것이 의도다.** 그래야 새로고침이 같은 자리로 돌아오고, 저장소가 막힌 환경
 * (사파리 프라이빗)에서도 `?ref=` 를 잃지 않는다.
 *
 * 그래도 `/login` 을 표에 두는 이유는 그 주소로 직접 들어오는 사람이 있어서다
 * (북마크·로그아웃 뒤 뒤로 가기). 이미 로그인돼 있으면 그 화면은 할 말이 없고,
 * `App.tsx` 가 방송으로 되돌리는 안내를 그린다.
 *
 * ── `?debug=1` 은 시청 화면을 이기지 못한다 ────────────────────
 *
 * 흡수된 모바일 웹에서는 `?debug=1` 이 경로를 이겼다(어디서 켜든 진단 화면). 통합 뒤로는
 * **시청 화면이 예외다.** 그 화면의 진단 표면은 재생 위에 겹치는 `DebugPanel` 이고,
 * 무엇보다 실기 하네스 둘이 `/?debug=1` 에서 재생을 시작해 `window.__bloomReceiver` 로
 * 프레임 타이밍을 읽는다 — 여기서 진단 화면을 그리면 **T_freeze 측정이 통째로 사라진다.**
 *
 * 대신 진단 화면에 `/debug` 를 준다. iOS 는 홈 화면 앱에서 주소를 바꿀 수 없어
 * 쿼리로는 그 화면에 갈 방법이 없고(`docs/11`), 설정 시트의 링크가 그 자리를 맡는다.
 *
 * ── `/sim/stb` 는 꺼져 있으면 없는 경로다 (B11) ────────────────
 *
 * STB 모사는 **다른 사업자의 셋톱 앱**을 흉내 내는 화면이라 BLOOM 의 화면이 아니다
 * (`docs/14` B11). 배포 설정(`VITE_ENABLE_MODE_A_SIM`)이 꺼져 있으면 시청 화면으로
 * 보낸다 — 404 를 만들지 않는 것은 다른 모르는 경로와 같은 방침이다.
 *
 * **판정을 여기 둔다.** 화면 쪽에서 「경로는 맞는데 안 그린다」로 처리하면 규칙이 두
 * 곳으로 갈리고, 그중 하나는 순수 함수 밖이라 검사되지 않는다.
 *
 * 끝의 `/` 는 떼고 본다. `/install/` 로 들어오는 경우가 실재한다 (사용자가 주소창에
 * 손으로 넣거나, 프록시가 붙이거나).
 */
export function resolvePage(pathname: string, options: RouteOptions = {}): Page {
  const page = ROUTES[normalizePath(pathname)] ?? 'watch';
  if (page === 'stb-sim' && options.modeASim !== true) return 'watch';
  if (options.debug === true && page !== 'watch') return 'debug';
  return page;
}

function normalizePath(pathname: string): string {
  if (pathname === '') return '/';
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}
